import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { formatDate } from '@utils/kpi-utils/date';

import { formatTemplateWithTime, timezoneUTC } from '@constants/common';

interface IGetColumns {
	isProjectActive: boolean;
	isPortalAdmin: boolean;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	setEditableMilestoneId: React.Dispatch<React.SetStateAction<string | null>>;
	setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
	openDeleteConfirm: () => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
}

export const getColumns = ({
	isProjectActive,
	isPortalAdmin,
	setIsEditMode,
	setEditableMilestoneId,
	setIsViewMode,
	openDeleteConfirm,
	setSingleIdForRemove,
}: IGetColumns) => ([
	{
		header: 'Title',
		accessorKey: 'title',
		cell: ({ getValue }: CellContext<any, any>) => {
			const title = getValue();

			return <AdaptiveTableCell text={title} />;
		},
	},
	{
		header: 'Status',
		accessorKey: 'status',
		cell: ({ getValue }: CellContext<any, any>) => {
			const status = getValue();

			return <AdaptiveTableCell text={status?.title} />;
		},
	},
	{
		header: 'Number Of Tasks',
		accessorKey: 'numberOfTasks',
		cell: ({ getValue }: CellContext<any, any>) => {
			const numberOfTasks = getValue();

			return <AdaptiveTableCell text={numberOfTasks} />;
		},
	},
	{
		header: 'Assignees',
		accessorKey: 'assignees',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();
			const assignees = value?.map(({ fullName }: { fullName: string }) => fullName).join(', ');

			return <AdaptiveTableCell text={assignees} />;
		},
	},
	{
		header: 'Start date',
		accessorKey: 'startDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const startDate = getValue();

			return <AdaptiveTableCell text={startDate ? startDate : '-'} />;
		},
	},
	{
		header: 'End date',
		accessorKey: 'endDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const endDate = getValue();

			return <AdaptiveTableCell text={endDate ? endDate : '-'} />;
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const createdAt = getValue();
			const date = createdAt ? formatDate(utcToZonedTime(createdAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Updated',
		accessorKey: 'updatedAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const updatedAt = getValue();
			const date = updatedAt ? formatDate(utcToZonedTime(updatedAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			const handleEdit = () => {
				setEditableMilestoneId(row?.original.id);
				setIsEditMode(true);
			};

			const handleView = () => {
				setEditableMilestoneId(row?.original.id);
				setIsViewMode(true);
			};

			const handleDelete = () => {
				setSingleIdForRemove(row?.original?.id);
				openDeleteConfirm();
			};

			return (
				<Actions
					deleteTooltip='Delete milestone'
					editHandler={handleEdit}
					editTooltip='Edit milestone'
					handleDelete={handleDelete}
					isDelete={isProjectActive && isPortalAdmin}
					isEdit={isProjectActive}
					isView={!isProjectActive}
					viewHandler={handleView}
					viewTooltip='View milestone'
				/>
			);
		},
	},
]);
